import {
  alpha,
  buttonClasses,
  createTheme,
  darken,
  formLabelClasses,
  lighten,
  outlinedInputClasses,
} from '@mui/material';

// The brand families are the CSS variables defined in index.css
export const fonts = {
  body: 'var(--font-body)',
  heading: 'var(--font-heading)',
  mono: 'var(--font-mono)',
};

// The greys of the 2026 brand guidelines; 800 is Black Charcoal
export const neutral = {
  50: '#F8F8F8',
  100: '#F2F2F2',
  200: '#D9D9D9',
  300: '#CFCFCF',
  400: '#A3A3A3',
  500: '#8C8C8C',
  600: '#6E6E6E',
  700: '#4A4A4A',
  800: '#313131',
  900: '#131313',
};

// Blue Ice, the brand's primary color; the guidelines define no shades, so they are derived from it
const BLUE_ICE = '#1057FA';

const ERROR = '#F04438';

// Sizes follow the 2026 brand guidelines, where headings scale down on small screens
const desktop = '@media (min-width:900px)';

export const theme = createTheme({
  palette: {
    action: {
      active: neutral[500],
      disabled: alpha(neutral[900], 0.38),
      disabledBackground: alpha(neutral[900], 0.12),
      focus: alpha(neutral[900], 0.16),
      hover: alpha(neutral[900], 0.04),
      selected: alpha(neutral[900], 0.12),
    },
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
    },
    divider: '#CCCCCC',
    error: {
      main: ERROR,
      dark: '#B42318',
      contrastText: '#FFFFFF',
    },
    neutral,
    primary: {
      main: BLUE_ICE,
      light: lighten(BLUE_ICE, 0.4),
      dark: darken(BLUE_ICE, 0.2),
      contrastText: '#FFFFFF',
    },
    text: {
      primary: neutral[800],
      secondary: neutral[700],
      disabled: neutral[400],
    },
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily: fonts.body,
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.4,
      letterSpacing: '-0.14px',
    },
    button: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: '23.8px',
      letterSpacing: '0.14px',
      textTransform: 'none',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.4,
    },
    subtitle1: {
      fontSize: '1.125rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.57,
    },
    overline: {
      fontFamily: fonts.mono,
      fontSize: '0.75rem',
      fontWeight: 400,
      letterSpacing: 0,
      lineHeight: 1.3,
      textTransform: 'uppercase',
    },
    h1: {
      fontFamily: fonts.heading,
      fontWeight: 400,
      fontSize: '2.5rem',
      lineHeight: 1.05,
      [desktop]: {
        fontSize: '3.625rem',
        lineHeight: '56px',
      },
    },
    h2: {
      fontFamily: fonts.heading,
      fontWeight: 400,
      fontSize: '2rem',
      lineHeight: 1.1,
      [desktop]: {
        fontSize: '2.5rem',
      },
    },
    h3: {
      fontFamily: fonts.heading,
      fontWeight: 400,
      fontSize: '1.5rem',
      lineHeight: 1.2,
    },
    h4: {
      fontFamily: fonts.heading,
      fontWeight: 400,
      fontSize: '1.3125rem',
      lineHeight: 1.2,
    },
    h5: {
      fontFamily: fonts.heading,
      fontWeight: 400,
      fontSize: '1.125rem',
      lineHeight: '24px',
    },
    h6: {
      fontFamily: fonts.heading,
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.3,
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
        },
        // The brand's primary CTA: the label on the left and the arrow on the right
        sizeLarge: {
          minHeight: 54,
          padding: '0 18px 0 24px',
          justifyContent: 'space-between',
          gap: 24,
          [`& .${buttonClasses.endIcon}`]: {
            marginRight: 0,
          },
          [`& .${buttonClasses.endIcon} > *:nth-of-type(1)`]: {
            fontSize: 24,
          },
        },
        // A white button with a hairline border, like the brand's arrow buttons
        outlinedInherit: {
          borderColor: neutral[300],
          backgroundColor: '#FFFFFF',
          color: neutral[800],
          '&:hover': {
            borderColor: neutral[400],
            backgroundColor: neutral[50],
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: neutral[600],
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          marginRight: 0,
          marginTop: 6,
          color: neutral[600],
          fontSize: 12.5,
          lineHeight: 1.4,
        },
      },
    },
    // Labels sit above their fields, as in the forms on keystrike.com
    MuiInputLabel: {
      defaultProps: {
        shrink: true,
      },
      styleOverrides: {
        root: {
          position: 'relative',
          transform: 'none',
          maxWidth: '100%',
          marginBottom: 6,
          color: neutral[800],
          fontSize: 13.5,
          fontWeight: 500,
          lineHeight: '20px',
          [`&.${formLabelClasses.focused}, &.${formLabelClasses.error}`]: {
            color: neutral[800],
          },
          [`&.${formLabelClasses.disabled}`]: {
            color: neutral[700],
          },
        },
      },
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: 15,
        },
      },
    },
    MuiOutlinedInput: {
      // The label sits above the field, so the border needs no gap for it
      defaultProps: {
        notched: false,
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: '#FFFFFF',
          transition: 'box-shadow 0.2s',
          [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
            borderColor: neutral[400],
          },
          [`&.${outlinedInputClasses.focused}`]: {
            boxShadow: `0 0 0 3px ${alpha(BLUE_ICE, 0.15)}`,
          },
          [`&.${outlinedInputClasses.focused} .${outlinedInputClasses.notchedOutline}`]: {
            borderColor: BLUE_ICE,
            borderWidth: 1,
          },
          [`&.${outlinedInputClasses.error} .${outlinedInputClasses.notchedOutline}`]: {
            borderColor: ERROR,
          },
          [`&.${outlinedInputClasses.error}.${outlinedInputClasses.focused}`]: {
            boxShadow: `0 0 0 3px ${alpha(ERROR, 0.15)}`,
          },
          [`&.${outlinedInputClasses.disabled}`]: {
            backgroundColor: neutral[50],
          },
          [`&.${outlinedInputClasses.disabled} .${outlinedInputClasses.notchedOutline}`]: {
            borderColor: neutral[200],
          },
        },
        input: {
          padding: '12px 14px',
          fontSize: 15,
          '&::placeholder': {
            color: neutral[400],
            opacity: 1,
          },
          [`&.${outlinedInputClasses.disabled}`]: {
            WebkitTextFillColor: neutral[600],
          },
        },
        notchedOutline: {
          borderColor: neutral[200],
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          border: `1px solid ${neutral[200]}`,
          boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.08)',
        },
      },
    },
  },
});
